import { RegisterCard } from "../components/RegisterCard";
import First from "../components/registerStaff/First";
import Second from "../components/registerStaff/Second";
import { TopAdminNav } from "../layouts/TopAdminNav";
import { useState, useEffect } from "react";
import BackButton from "../components/backButton";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { defaultUrl } from "../features/actions";
import {
  uploadImageToCloudinary,
  validateUploadFile,
  validatePhoneNumber,
} from "../utilities/importantFns";
import { makeFetchRequest, makePostRequest, makePutRequest } from "../utilities/common";
import { Check, User, MapPin } from "lucide-react";
import Swal from "sweetalert2";

const STAFF_DRAFT_KEY = "staff_onboarding_draft";
const STAFF_DRAFT_DB = "staff_onboarding_files";
const STAFF_DRAFT_STORE = "files";

const defaultStaffForm = {
  surname: "",
  firstname: "",
  othername: "",
  gender: "",
  date_of_birth: "",
  email: "",
  phone_number: "",
  classroom_id: "",
  house_address: "",
  house_address2: "",
  city: "",
  signature: "",
  state: "",
  country: "",
  zip_code: "",
  profile_picture: "",
  school_id: "",
  degree: "",
  discipline: "",
  class_type: "Secondary",
  institution: "DEFAULT",
  staff_type: "teaching",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  emergency_contact_relationship: "",
};

const openStaffDraftDb = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(STAFF_DRAFT_DB, 1);

    request.onupgradeneeded = () => {
      request.result.createObjectStore(STAFF_DRAFT_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const saveDraftFile = async (name: string, file: File) => {
  const db = await openStaffDraftDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STAFF_DRAFT_STORE, "readwrite");
    transaction.objectStore(STAFF_DRAFT_STORE).put(file, name);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
  db.close();
};

const getDraftFile = async (name: string) => {
  const db = await openStaffDraftDb();
  const file = await new Promise<File | undefined>((resolve, reject) => {
    const request = db.transaction(STAFF_DRAFT_STORE, "readonly")
      .objectStore(STAFF_DRAFT_STORE)
      .get(name);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  db.close();
  return file;
};

const clearStaffDraft = async () => {
  localStorage.removeItem(STAFF_DRAFT_KEY);
  const db = await openStaffDraftDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STAFF_DRAFT_STORE, "readwrite");
    transaction.objectStore(STAFF_DRAFT_STORE).clear();
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
  db.close();
};

export const RegisterTeacher = ({ editForm }: any) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [draftReady, setDraftReady] = useState(Boolean(editForm));

  const [form, setForm] = useState<any>(editForm ?? defaultStaffForm);

  useEffect(() => {
    if (editForm) return;

    const restoreDraft = async () => {
      const savedDraft = localStorage.getItem(STAFF_DRAFT_KEY);
      const savedFields = savedDraft ? JSON.parse(savedDraft) : {};
      const [profilePicture, signature] = await Promise.all([
        getDraftFile("profile_picture"),
        getDraftFile("signature"),
      ]);

      setForm((current: any) => ({
        ...current,
        ...savedFields,
        ...(profilePicture ? { profile_picture: profilePicture } : {}),
        ...(signature ? { signature } : {}),
      }));
    };

    restoreDraft()
      .catch(() => {
        toast.error("Could not restore the saved staff draft.");
      })
      .finally(() => setDraftReady(true));
  }, [editForm]);

  useEffect(() => {
    if (editForm || !draftReady) return;

    const draftFields = Object.fromEntries(
      Object.entries(form).filter(
        ([, value]) => !(value instanceof File) && !(value instanceof Blob),
      ),
    );

    localStorage.setItem(STAFF_DRAFT_KEY, JSON.stringify(draftFields));
  }, [draftReady, editForm, form]);

  const handleChange = ({ target: { name, value, files } }: any) => {
    if (files && files.length > 0) {
      const file = files[0];
      if (file.size > 10000000) {
        showValidationModal("File too large. 9MB limit.");
        return;
      }
      const validation = validateUploadFile(file, "image");
      if (!validation.valid) {
        showValidationModal(validation.message);
        return;
      }
      setForm({ ...form, [name]: file });
      saveDraftFile(name, file).catch(() => {
        toast.error("Could not save this file to the draft.");
      });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const getTeacherIds = (teachers: any[] = []) =>
    teachers
      .map((teacher: any) => {
        if (typeof teacher === "string") return teacher;
        return teacher?.id;
      })
      .filter(Boolean);

  const isBlank = (value: any) => !String(value || "").trim();

  const hasRealFileOrUrl = (value: any) => {
    return value instanceof File || (typeof value === "string" && value.trim());
  };

  const showValidationModal = (message: string) => {
    Swal.fire({
      title: "Complete Required Fields",
      text: message,
      icon: "error",
      confirmButtonColor: "#012063",
      confirmButtonText: "Review Form",
    });
  };

  const showMissingFields = (fieldLabels: string[]) => {
    showValidationModal(`Please complete: ${fieldLabels.join(", ")}`);
  };

  const validateStepOne = () => {
    const requiredFields: Record<string, string> = {
      surname: "Surname",
      firstname: "First name",
      othername: "Other names",
      email: "Email address",
      phone_number: "Mobile number",
      gender: "Gender",
      date_of_birth: "Date of birth",
      classroom_id: "Classroom",
      degree: "Qualification",
      discipline: "Discipline",
      signature: "Digital signature",
    };
    const missing = Object.entries(requiredFields)
      .filter(([field]) =>
        field === "signature" ? !hasRealFileOrUrl(form[field]) : isBlank(form[field]),
      )
      .map(([, label]) => label);

    if (missing.length) {
      showMissingFields(missing);
      return false;
    }

    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      showValidationModal("Please enter a valid email address.");
      return false;
    }

    if (!validatePhoneNumber(form.phone_number)) {
      showValidationModal("Invalid phone number format.");
      return false;
    }

    return true;
  };

  const validateStepTwo = () => {
    const requiredFields: Record<string, string> = {
      house_address: "Street address",
      city: "City",
      state: "State",
      country: "Country",
      emergency_contact_name: "Emergency contact name",
      emergency_contact_phone: "Emergency contact phone",
      emergency_contact_relationship: "Emergency contact relationship",
      profile_picture: "Profile picture",
    };
    const missing = Object.entries(requiredFields)
      .filter(([field]) =>
        field === "profile_picture"
          ? !hasRealFileOrUrl(form[field])
          : isBlank(form[field]),
      )
      .map(([, label]) => label);

    if (missing.length) {
      showMissingFields(missing);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!validateStepOne() || !validateStepTwo()) return;

    setLoading(true);
    const toastId = toast.loading("Processing registration...");

    try {
      const currentClassroom = await makeFetchRequest(
        `${defaultUrl}/classroom/${form.classroom_id}`,
      );
      let profileUrl = form.profile_picture;
      let signatureUrl = form.signature;

      if (form.profile_picture instanceof File) {
        profileUrl = await uploadImageToCloudinary(form.profile_picture);
      }
      if (form.signature instanceof File) {
        signatureUrl = await uploadImageToCloudinary(form.signature);
      }

      const body = {
        ...form,
        surname: form.surname.trim(),
        firstname: form.firstname.trim(),
        othername: form.othername.trim(),
        classroom_id: [form.classroom_id],
        profile_picture: profileUrl || "",
        signature: signatureUrl || "",
      };

      const res = await makePostRequest(`${defaultUrl}/teachers/create`, body);

      if (res?.teacher_id) {
        const currentTeacherIds = getTeacherIds(currentClassroom?.teachers || []);
        const newTeacherIds = Array.isArray(res.teacher_id)
          ? res.teacher_id
          : [res.teacher_id];
        const data = {
          name: currentClassroom?.name,
          teachers: Array.from(new Set([...currentTeacherIds, ...newTeacherIds])),
        };
        await makePutRequest(
          `${defaultUrl}/classroom/${currentClassroom?.id}`,
          data,
        );
        clearStaffDraft().catch(() => undefined);
        toast.success("Staff registered successfully!", { id: toastId });
        navigate("/admin/staff/staff_data");
      }
    } catch (error) {
      toast.error("Registration failed. Please try again.", { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const nextStep = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (validateStepOne()) setStep(2);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="w-full flex items-center p-4 bg-white border-b border-slate-100">
        <BackButton />
        <TopAdminNav name="Jane Doe" title="Create Staff Form" />
      </div>

      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="mb-8">
          <RegisterCard
            title="Staff Onboarding"
            details="Complete the form to register a new staff member."
          />
        </div>

        {/* Stepper UI */}
        <div className="flex items-center justify-center mb-10 space-x-4">
          <div
            className={`flex items-center space-x-2 p-2 px-4 rounded-full transition-all ${step === 1 ? "bg-blue-600 text-white shadow-lg" : "bg-white text-slate-400"}`}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === 1 ? "bg-white text-blue-600" : "bg-slate-100"}`}
            >
              1
            </div>
            <span className="text-sm font-bold">Personal Info</span>
          </div>
          <div className="w-12 h-[2px] bg-slate-200"></div>
          <div
            className={`flex items-center space-x-2 p-2 px-4 rounded-full transition-all ${step === 2 ? "bg-blue-600 text-white shadow-lg" : "bg-white text-slate-400"}`}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === 2 ? "bg-white text-blue-600" : "bg-slate-100"}`}
            >
              2
            </div>
            <span className="text-sm font-bold">Address & Emergency</span>
          </div>
        </div>

        <div className="bg-white rounded-[2.5rem] shadow-xl border border-slate-100 overflow-hidden">
          {step === 1 ? (
            <First
              nextStep={nextStep}
              handleChange={handleChange}
              form={form}
              next_form={() => setStep(2)}
            />
          ) : (
            <Second
              prevStep={() => setStep(1)}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              form={form}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
};
